<script setup lang="ts">
import { useAppStore } from '@/store';

const route = useRoute();
const router = useRouter();

const currEnv = import.meta.env.MODE;

const opened = ref<string[]>([]);

const props = defineProps<{
  modelValue: boolean;
}>();
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const appStore = useAppStore();
const { userMenuList } = storeToRefs(appStore);

const findActiveGroup = (currentPath: string): string | null => {
  for (const menu of userMenuList.value) {
    if (menu.menuList) {
      for (const child of menu.menuList) {
        // 정확한 경로 매칭 또는 경로 시작 부분 매칭
        if (currentPath === child.path || currentPath.startsWith(child.path + '/')) {
          return menu.name;
        }
      }
    }
  }
  return null;
};

const openActiveGroup = () => {
  setTimeout(() => {
    const activeGroup = findActiveGroup(route.path);
    if (activeGroup) {
      // 방법 1: 클릭 이벤트 트리거
      const groupElement = document.querySelector(`[id="v-list-group--id-${activeGroup}"]`);
      if (groupElement) {
        (groupElement as HTMLElement).click();
      }

      // 방법 2: CSS 직접 조작 (백업 방법)
      setTimeout(() => {
        const listGroup = document
          .querySelector(`[id="v-list-group--id-${activeGroup}"]`)
          ?.closest('.v-list-group');
        const itemsContainer = listGroup?.querySelector('.v-list-group__items');

        if (listGroup && itemsContainer) {
          listGroup.classList.add('v-list-group--open');
          (itemsContainer as HTMLElement).style.display = '';
        }
      }, 50);
    }
  }, 100);
};
const controlActiveGroup = () => {
  if (props.modelValue) {
    openActiveGroup();
  }
  emit('update:modelValue', !props.modelValue);
  opened.value = [];
};
onMounted(async () => {
  openActiveGroup();
});
</script>

<template>
  <v-navigation-drawer
    left
    color="primary"
    class="AppSideNav"
    style="position: fixed !important"
    :rail="props.modelValue"
    rail-width="56"
    permanent
    width="220"
  >
    <template #prepend>
      <div
        class="d-flex align-center menu-header"
        :class="{ 'justify-center': props.modelValue, 'justify-space-between': !props.modelValue }"
        :style="{ padding: !props.modelValue ? '20px 0 20px 18px' : '0' }"
      >
        <div
          v-if="!props.modelValue"
          class="d-flex align-center justify-center"
          @click="router.push('/')"
          style="cursor: pointer"
        >
          <img src="@/assets/images/logo/logo-white.svg" alt="" />
        </div>
        <div v-if="!props.modelValue" class="menu-controller" @click="controlActiveGroup">
          <v-icon>custom:menuClose</v-icon>
        </div>
        <div v-else class="menu-controller" @click="controlActiveGroup">
          <v-icon>custom:menuOpen</v-icon>
        </div>
      </div>
    </template>
    <v-list
      nav
      class="AppSideNav__menus"
      open-strategy="single"
      v-model:opened="opened"
      active-class="isActive"
    >
      <template v-for="(main, idx) in userMenuList" :key="idx">
        <!-- {{ main.urlPath }} -->

        <v-list-group
          :value="main.name"
          expand-icon="custom:arrowDown"
          collapse-icon="custom:arrowUp"
        >
          <template v-slot:activator="{ props }">
            <v-list-item
              v-bind="props"
              :value="main.name"
              :prepend-icon="main.icon"
              :title="main.name"
              @click="emit('update:modelValue', false)"
              :class="{
                isActive:
                  (main.menuList && route.fullPath == main.menuList[0].path) ||
                  (main.menuList && main.menuList.some((c) => c.path == route.fullPath)),
              }"
            ></v-list-item>
          </template>
          <v-list-item
            v-for="child in main.menuList"
            :title="child.name"
            @click="router.push(child.path ?? '')"
            :key="child.name"
            :class="{ isActive: route.fullPath == child.path }"
          ></v-list-item>
        </v-list-group>
      </template>
    </v-list>
  </v-navigation-drawer>
</template>

<style lang="scss">
$size: 16px;
.menu-controller {
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  width: 52px;
  height: 50px;
}
.menu-header {
  height: 50px;
}

.AppSideNav {
  &.v-navigation-drawer {
    border-right-width: 0;
  }
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-inline-start: 20px;
    height: 50px;
  }
  &__logo {
    transition: all ease-in-out 0.3s;
    img {
      height: 20px;
    }
    span {
      display: block;
      margin-top: 2px;
      color: $body-light;
      font-style: normal;
      font-weight: 700;
      font-size: 12px;
      line-height: 140%;
    }
  }
  &__menus {
    margin-top: 24px;
    &.v-list {
      background-color: $primary;
      padding: 0;
      .v-list-item {
        grid-template-columns: 36px 1fr auto;
        margin-bottom: 0 !important;
        padding-inline: 20px 18px;
        min-height: 58px;
        color: $body-light;
        .v-list-item-title {
          color: inherit;
          font-style: normal;
          font-weight: 400;
          font-size: $size !important;
          line-height: 140%;
        }

        &.isActive {
          color: $white;
          .v-list-item__overlay {
            opacity: 0;
          }
        }
        .v-list-item__prepend {
          .v-icon {
            opacity: 1 !important;
            color: inherit !important;
            font-size: calc($size * 1.125);
          }
        }
      }
      .v-list-group {
        .v-list-item__append > .v-icon {
          opacity: 1;
          font-size: $size !important;
        }
        .v-list-group__items {
          .v-list-item {
            grid-template-columns: 1fr auto;
            grid-template-areas: 'content append';
            border-radius: 0;
            padding-inline-start: 56px !important;
            min-height: 58px;
            .v-list-item-title {
              font-style: normal;
              font-weight: 400;
              font-size: $size;
              line-height: 140%;
            }
            &.isActive {
              // &--active {
              background-color: #313131 !important;

              .v-list-item-title {
              }
              .v-list-item__overlay {
                opacity: 0;
              }
            }
          }
        }
      }
    }
  }
}
</style>
